import { createTheme } from '@material-ui/core'

export const customColor = {
  // Blacks, Grays, and Whites
  black: '#000000',
  almostBlack: '#181818',
  lighterBlack: '#303030',
  darkBlack: '#000000',
  darkGray: '#444444',
  darkSlateGray: '#313131',
  darkerSlateGray: '#232322',
  primaryHeaderText: '#2d2d2e',
  gray: '#737373',
  secondaryHeaderText: '#737373',
  midGray: '#888888',
  secondaryHeaderTextDark: '#c0c0c0',
  lightGray: '#cbcbcb',
  gainsBoro: '#DCDCDC',
  whitesmoke: '#eeeeee',
  lighterGray: '#f3f3f3',
  lighterWhitesmoke: '#fcfcfc',
  white: '#ffffff',
  primaryHeaderTextDark: '#ffffff',
  linearGradientWhite: 'linear-gradient(#FDFDFD, #EEEEEE)',

  // Reds and Pinks
  darkerRed: '#9a0126',
  rutgersRed: '#cc0033',
  sideNavBackgroundColor: '#DD3333',
  lighterRed: '#DD3333',
  darkerPink: '#D66682',
  sideNavColor: '#E79494',
  rutgersLightPink: '#ffe3eb',

  // Oranges
  orange: '#cc5500',

  // Yellows
  yellow: '#f9c642',
  brightYellow: '#FDDA24',

  // Greens and Teals
  green: '#00b339',
  parkingBlue: '#00626D',
  parkingButtonDisabledBlue: '#87C3BE',

  // Blues
  blue: '#2F6FF3',
  bluePart2: '#558BF7',
  headerBlue: '#0F61E6',
  darkBlue: '#002aac',
  blueGray: '#5d6799',
  rcsaBlue: '#006FA8',
  allParkingBlue: '#007FAC',
  libraryQuickSearchCard: '#1e70bf',
  linkText: '#3878B7',
  lightParkingBlue: '#89BED5',
  linkTextDark: '#91DAFF',
  paleBlue: '#92D6E3',
  lightBlue: '#DEF0F9',
  tealDark: '#1A74A1',
  tealMedium: '#3896BB',
  tealLight: '#52A6C4',
  tealPale: '#BDE6F5',
  transparent: 'rgba(0, 0, 0, 0)',
}

export function theme(dark) {
  const palleteType = dark ? 'dark' : 'light'
  const mainPrimaryColor = dark ? customColor.white : customColor.rutgersRed
  const mainPrimaryContast = dark ? customColor.rutgersRed : customColor.white

  const headerTextPrimaryColor = dark
    ? customColor.primaryHeaderTextDark
    : customColor.primaryHeaderText

  const mainSecondaryColor = dark ? customColor.white : customColor.blue
  const mainSecondaryContast = dark ? customColor.blue : customColor.white

  const headerTextSecondaryColor = dark
    ? customColor.secondaryHeaderTextDark
    : customColor.secondaryHeaderText

  const mainBackground = dark ? customColor.almostBlack : customColor.whitesmoke
  const secondaryBackgroundColor = dark
    ? customColor.darkSlateGray
    : customColor.white
  const ternaryBackground = dark
    ? customColor.darkGray
    : customColor.lighterGray

  const palette = {
    color: customColor,
    palette: {
      type: palleteType,
      primary: {
        main: mainPrimaryColor,
        contrastText: mainPrimaryContast,
        headerText: headerTextPrimaryColor,
      },
      secondary: {
        main: mainSecondaryColor,
        contrastText: mainSecondaryContast,
        headerText: headerTextSecondaryColor,
      },
      paper: {
        primary: dark ? customColor.darkerSlateGray : customColor.white,
        secondary: dark ? customColor.darkGray : customColor.lighterGray,
        tertiary: dark ? customColor.darkGray : customColor.lightGray,
        dragAndDrop: dark ? customColor.darkGray : customColor.white,
      },
      base: {
        // Blacks, Grays, and Whites
        black: customColor.black,
        darkGray: customColor.darkGray,
        gray: customColor.gray,
        lightGray: customColor.lightGray,
        white: customColor.white,
        // Colors in ROYGBIV order
        red: customColor.rutgersRed,
        orange: customColor.orange,
        yellow: customColor.yellow,
        brightYellow: customColor.brightYellow,
        green: customColor.green,
        blue: dark ? customColor.bluePart2 : customColor.blue,
        lightBlue: customColor.lightBlue,
        blueGray: customColor.blueGray,
      },
      backgroundColor: {
        default: customColor.gainsBoro,
        main: mainBackground,
        secondary: secondaryBackgroundColor,
        ternary: ternaryBackground,
      },
      components: {
        appBar: dark
          ? customColor.darkerSlateGray
          : customColor.primaryHeaderTextDark,
        sideNav: dark ? customColor.almostBlack : customColor.rutgersRed,
        icon: dark ? customColor.white : customColor.gray,
        sideNavIcon: dark ? customColor.white : customColor.white,
        topBarIcon: dark ? customColor.gray : customColor.rutgersRed,
        sideNavHover: dark ? customColor.darkSlateGray : customColor.lighterRed,
        tabBackground: dark
          ? customColor.darkerSlateGray
          : customColor.whitesmoke,
        newsBackground: dark
          ? customColor.darkerSlateGray
          : customColor.whitesmoke,
        newsArticleBackground: dark
          ? customColor.darkGray
          : customColor.whitesmoke,
        newsSectionBackground: dark
          ? customColor.darkerSlateGray
          : customColor.linearGradientWhite,
        bottomSheetOverlay: dark
          ? customColor.lighterWhitesmoke
          : customColor.white,
        rcsaBlue: dark ? customColor.white : customColor.rcsaBlue,
        linkText: dark ? customColor.linkTextDark : customColor.linkText,
        linkTextAlt: dark ? customColor.linkTextDark : customColor.black,
        listBackground: dark ? customColor.darkGray : customColor.gainsBoro,
        parkingLotIcons: customColor.parkingBlue,
        parkingLotDisabledIcons: customColor.parkingButtonDisabledBlue,
        allParkingButtonEnabled: customColor.allParkingBlue,
        allParkingButtonDisabled: customColor.lightParkingBlue,
        busStopsIconDisabled: customColor.darkerPink,
        additionalSettingsButtonDisabled: customColor.midGray,
        navigationRoutesButton: dark ? customColor.black : customColor.white,
        linkList: dark ? customColor.white : customColor.almostBlack,
        linkListBackground: dark
          ? customColor.darkerSlateGray
          : customColor.lightBlue,
        linkListBackgroundInverse: dark
          ? customColor.darkerSlateGray
          : customColor.lighterGray,
        linkListDarkHover: dark ? customColor.white : customColor.almostBlack,
        emergencyIcons: dark ? customColor.white : customColor.darkBlue,
        visitorLabel: dark ? customColor.lightBlue : customColor.darkBlue,
      },
      button: {
        hover: dark ? customColor.darkGray : customColor.lightGray,
        hoverLight: dark ? customColor.lightGray : customColor.lighterGray,
        hoverDark: dark
          ? customColor.secondaryHeaderText
          : customColor.lighterGray,
        hoverDarkInverted: dark ? customColor.gainsBoro : customColor.darkGray,
        focusOutline: customColor.blue,
        focusBorder: dark ? customColor.darkSlateGray : customColor.white,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 769,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
  }

  const breakpoints = createTheme(palette)

  palette.overrides = {
    MuiButton: {
      root: {
        // all MUI buttons will now have a slight fade and scale on hover
        transition: breakpoints.transitions.create(
          ['background-color', 'filter', 'transform'],
          {
            duration: breakpoints.transitions.duration.short,
          },
        ),
        '&:hover': {
          backgroundColor: null,
          filter: 'brightness(85%)',
          transform: 'scale(1.04)',
          cursor: 'pointer',
        },
      },
    },
    MuiChip: {
      root: {
        '&:hover': {
          filter: 'brightness(100%)',
        },
      },
    },
    MuiAlert: {
      filledError: {
        backgroundColor: '#cc0033',
      },
      filledWarning: {
        backgroundColor: '#a86500',
      },
    },
  }

  palette.typography = {
    fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
    h1: {
      fontWeight: 400,
      fontSize: '2.5rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '2.00rem',
      },
    },
    h2: {
      fontWeight: 400,
      fontSize: '2.25rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontWeight: 400,
      fontSize: '1.75rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '1.50rem',
      },
    },
    h4: {
      fontWeight: 400,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 400,
      fontSize: '1.25rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    h6: {
      fontWeight: 400,
      fontSize: '1.125rem',
      letterSpacing: '-0.025em',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.875rem',
      },
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.875rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
    button: {
      textTransform: 'none',
      fontSize: '0.875rem',
      fontWeight: 500,
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.875rem',
      [breakpoints.breakpoints.down('sm')]: {
        fontSize: '0.75rem',
      },
    },
    overline: {},
  }

  return createTheme(palette)
}

export default theme
